/**
 * ConnectBrowserAuthModal — Browserbase-powered browser login flow.
 *
 * Embeds a remote browser session for SSO/manual login,
 * auto-detects completion, and stores browser cookies.
 * Works for Read.ai, LinkedIn, and any future browser-auth provider.
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { X, Loader2, CheckCircle2, AlertTriangle, ExternalLink, RefreshCw } from 'lucide-react';

interface ConnectBrowserAuthModalProps {
  open: boolean;
  onClose: () => void;
  onConnected?: () => void;
  /** Provider ID — determines API endpoints and UI labels */
  provider: 'readai' | 'linkedin' | 'claude';
}

type Phase = 'idle' | 'initializing' | 'waiting' | 'connected' | 'error';

const POLL_INTERVAL = 2500;

const PROVIDER_META: Record<string, { name: string; color: string; letter: string; waitingText: string; connectedText: string }> = {
  readai: {
    name: 'Read.ai',
    color: 'indigo',
    letter: 'R',
    waitingText: 'Navigate to app.read.ai and sign in with Google. We\'ll detect when you\'re done.',
    connectedText: 'Your Read.ai account is connected. Martha can now retrieve meeting transcripts, summaries, and video recordings.',
  },
  linkedin: {
    name: 'LinkedIn',
    color: 'blue',
    letter: 'in',
    waitingText: 'Sign in to your LinkedIn account. We\'ll detect when you\'re done.',
    connectedText: 'Your LinkedIn account is connected. You can publish and manage posts directly from Martha.',
  },
  claude: {
    name: 'Claude',
    color: 'amber',
    letter: 'C',
    waitingText: 'Sign in to your Claude.ai account. We\'ll detect when you\'re done.',
    connectedText: 'Your Claude account is connected. You can now paste claude.ai URLs into topics to fetch conversations and artifacts.',
  },
};

export function ConnectBrowserAuthModal({ open, onClose, onConnected, provider }: ConnectBrowserAuthModalProps) {
  const meta = PROVIDER_META[provider] || PROVIDER_META.readai;
  const [phase, setPhase] = useState<Phase>('idle');
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [sessionUrl, setSessionUrl] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // ── Initialize Browserbase session ──────────────────────────
  const startSession = useCallback(async () => {
    setPhase('initializing');
    setErrorMessage(null);

    try {
      const res = await fetch(`/api/integrations/${provider}/browser/initialize`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: '{}',
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Session init failed: ${res.status}`);
      }

      const data = await res.json() as { sessionId: string; sessionUrl: string };
      setSessionId(data.sessionId);
      setSessionUrl(data.sessionUrl);
      setPhase('waiting');
    } catch (err) {
      setErrorMessage((err as Error).message);
      setPhase('error');
    }
  }, [provider]);

  // ── Poll for login completion ──────────────────────────────
  useEffect(() => {
    if (phase !== 'waiting' || !sessionId) return;

    const poll = async () => {
      try {
        const res = await fetch(
          `/api/integrations/${provider}/browser/poll/${sessionId}?workspace_id=default`,
          { credentials: 'include' },
        );
        if (!res.ok) return;
        const data = await res.json() as { isComplete: boolean };
        if (data.isComplete) {
          setPhase('connected');
          onConnected?.();
          if (pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
          }
        }
      } catch {}
    };

    poll();
    pollRef.current = setInterval(poll, POLL_INTERVAL);

    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [phase, sessionId, provider, onConnected]);

  // ── Auto-start on open ─────────────────────────────────────
  useEffect(() => {
    if (open && phase === 'idle') {
      startSession();
    }
  }, [open, phase, startSession]);

  // ── Cleanup on close ───────────────────────────────────────
  const handleClose = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    if (sessionId) {
      fetch(`/api/integrations/${provider}/browser/${sessionId}`, {
        method: 'DELETE',
        credentials: 'include',
      }).catch(() => {});
    }
    setPhase('idle');
    setSessionId(null);
    setSessionUrl(null);
    setErrorMessage(null);
    onClose();
  }, [sessionId, provider, onClose]);

  if (!open) return null;

  const accent = meta.color === 'blue' ? {
    bg: 'bg-blue-600', hover: 'hover:bg-blue-700',
    spinnerText: 'text-blue-600', barBg: 'bg-blue-50 dark:bg-blue-900/30',
    barBorder: 'border-blue-200 dark:border-blue-800', barText: 'text-blue-700 dark:text-blue-300',
    iconBg: 'bg-blue-600',
  } : {
    bg: 'bg-indigo-600', hover: 'hover:bg-indigo-700',
    spinnerText: 'text-indigo-600', barBg: 'bg-indigo-50 dark:bg-indigo-900/30',
    barBorder: 'border-indigo-200 dark:border-indigo-800', barText: 'text-indigo-700 dark:text-indigo-300',
    iconBg: 'bg-indigo-600',
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="relative w-full max-w-4xl mx-4 bg-white dark:bg-zinc-900 rounded-xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-200 dark:border-zinc-700">
          <div className="flex items-center gap-3">
            <div className={`w-8 h-8 rounded-lg ${accent.iconBg} flex items-center justify-center`}>
              <span className="text-white font-bold text-sm">{meta.letter}</span>
            </div>
            <div>
              <h2 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">Connect {meta.name}</h2>
              <p className="text-sm text-zinc-500">Sign in to enable {meta.name} integration</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-2 rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
          >
            <X className="w-5 h-5 text-zinc-500" />
          </button>
        </div>

        {/* Content */}
        <div className="relative" style={{ height: '560px' }}>
          {/* Initializing */}
          {phase === 'initializing' && (
            <div className="flex flex-col items-center justify-center h-full gap-4">
              <Loader2 className={`w-8 h-8 ${accent.spinnerText} animate-spin`} />
              <p className="text-zinc-600 dark:text-zinc-400">Starting secure browser session...</p>
            </div>
          )}

          {/* Waiting — show iframe */}
          {phase === 'waiting' && sessionUrl && (
            <>
              <iframe
                src={sessionUrl}
                className="w-full h-full border-0"
                allow="clipboard-write"
                sandbox="allow-same-origin allow-scripts allow-forms allow-popups"
              />
              <div className={`absolute bottom-0 left-0 right-0 px-4 py-2 ${accent.barBg} border-t ${accent.barBorder} flex items-center gap-2`}>
                <Loader2 className={`w-4 h-4 ${accent.spinnerText} animate-spin`} />
                <span className={`text-sm ${accent.barText}`}>
                  {meta.waitingText}
                </span>
              </div>
            </>
          )}

          {/* Connected */}
          {phase === 'connected' && (
            <div className="flex flex-col items-center justify-center h-full gap-4">
              <div className="w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                <CheckCircle2 className="w-10 h-10 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold text-zinc-900 dark:text-zinc-100">
                {meta.name} Connected
              </h3>
              <p className="text-zinc-600 dark:text-zinc-400 text-center max-w-md">
                {meta.connectedText}
              </p>
              <button
                onClick={handleClose}
                className={`mt-4 px-6 py-2 ${accent.bg} text-white rounded-lg ${accent.hover} transition-colors font-medium`}
              >
                Done
              </button>
            </div>
          )}

          {/* Error */}
          {phase === 'error' && (
            <div className="flex flex-col items-center justify-center h-full gap-4">
              <div className="w-16 h-16 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                <AlertTriangle className="w-10 h-10 text-amber-600" />
              </div>
              <h3 className="text-xl font-semibold text-zinc-900 dark:text-zinc-100">
                Connection Failed
              </h3>
              <p className="text-zinc-600 dark:text-zinc-400 text-center max-w-md">
                {errorMessage || 'Something went wrong. Please try again.'}
              </p>
              <div className="flex gap-3 mt-4">
                <button
                  onClick={startSession}
                  className={`flex items-center gap-2 px-4 py-2 ${accent.bg} text-white rounded-lg ${accent.hover} transition-colors font-medium`}
                >
                  <RefreshCw className="w-4 h-4" />
                  Try Again
                </button>
                <button
                  onClick={handleClose}
                  className="px-4 py-2 border border-zinc-300 dark:border-zinc-600 rounded-lg hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800/50">
          <p className="text-xs text-zinc-500 flex items-center gap-1.5">
            <ExternalLink className="w-3 h-3" />
            Secure session powered by Browserbase. Your session cookies are encrypted and stored locally.
          </p>
        </div>
      </div>
    </div>
  );
}

/** @deprecated Use ConnectBrowserAuthModal with provider="readai" */
export const ConnectReadAiModal = (props: Omit<ConnectBrowserAuthModalProps, 'provider'>) => (
  <ConnectBrowserAuthModal {...props} provider="readai" />
);
